"use client";

import { X } from "lucide-react";
import { useEffect, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

interface TeamMemberOption {
  id: string;
  name: string;
}

export interface ServiceFilters {
  status: "all" | "active" | "inactive";
  serviceType: string;
  employeeId: string;
  categoryId: string;
}

interface CategoryOption {
  id: string;
  name: string;
}

interface FiltersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: ServiceFilters) => void;
  teamMembers: TeamMemberOption[];
  serviceTypes: string[];
  initialFilters: ServiceFilters;
  categories: CategoryOption[];
}

export const FiltersModal = ({
  isOpen,
  onClose,
  onApply,
  teamMembers,
  serviceTypes,
  initialFilters,
  categories,
}: FiltersModalProps) => {
  const [status, setStatus] = useState<ServiceFilters["status"]>("all");
  const [serviceType, setServiceType] = useState<string>("all");
  const [employeeId, setEmployeeId] = useState<string>("all");
  const [categoryId, setCategoryId] = useState<string>("all");

  useEffect(() => {
    if (isOpen) {
      setStatus(initialFilters.status);
      setServiceType(initialFilters.serviceType);
      setEmployeeId(initialFilters.employeeId);
      setCategoryId(initialFilters.categoryId ?? "all");
    }
  }, [initialFilters, isOpen]);

  if (!isOpen) return null;

  const handleApply = () => {
    onApply({ status, serviceType, employeeId, categoryId });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden border border-gray-200">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-5 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Filters</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-130px)] px-6 py-6 space-y-6">
          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <Select
              value={status}
              onValueChange={(value) => setStatus(value as ServiceFilters["status"])}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Service Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Service Type
            </label>
            <Select value={serviceType} onValueChange={setServiceType}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select service type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {serviceTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category
            </label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Team Member */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Team Member
            </label>
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select team member" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any team member</SelectItem>
                {teamMembers.map((member, i) => (
                  <SelectItem key={member.id ?? i} value={member.id}>
                    {member.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center px-6 py-5 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-white hover:border-gray-400 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            className="px-8 py-2 rounded-lg bg-[#885ABB] hover:bg-[#6f47a2] text-white font-semibold transition-all"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};
